import type { PillProps } from '@/design-system';

export type SsoOidcStatus = {
  provider: string;
  source: 'db' | 'env' | 'none';
  tenantId: string | null;
  last4: string | null;
  rotatedAt: number | null;
  setByEmail: string | null;
};

export type SsoFormState = {
  tenantId: string;
  clientId: string;
  clientSecret: string;
};

export const EMPTY_SSO_FORM: SsoFormState = {
  tenantId: '',
  clientId: '',
  clientSecret: '',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const ROTATE_REMINDER_DAYS = 90;
const ROTATE_URGENT_DAYS = 540;

export function formatSsoTimestamp(epochMs: number | null): string {
  if (epochMs === null) return 'Never';
  return new Date(epochMs).toLocaleString();
}

export function formatSsoRelativeTime(epochMs: number | null, now: number): string {
  if (epochMs === null) return 'Never';
  const days = Math.floor(Math.max(0, now - epochMs) / DAY_MS);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 24) return months === 1 ? '1 month ago' : `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

export function rotationFreshness(
  rotatedAt: number | null,
  now: number,
): { tone: PillProps['tone']; label: string } | null {
  if (rotatedAt === null) return null;
  const days = Math.floor(Math.max(0, now - rotatedAt) / DAY_MS);
  if (days >= ROTATE_URGENT_DAYS) return { tone: 'danger', label: 'Rotate soon' };
  if (days >= ROTATE_REMINDER_DAYS) return { tone: 'warning', label: 'Consider rotating' };
  return { tone: 'success', label: 'Fresh' };
}

export function sourceLabel(source: SsoOidcStatus['source']): string {
  switch (source) {
    case 'db':
      return 'Admin panel';
    case 'env':
      return 'Environment variables';
    case 'none':
      return 'Not configured';
  }
}

export function sourceTone(source: SsoOidcStatus['source']): PillProps['tone'] {
  switch (source) {
    case 'db':
      return 'success';
    case 'env':
      return 'default';
    case 'none':
      return 'warning';
  }
}

export function fieldForValidationMessage(message: string): keyof SsoFormState | null {
  const lower = message.toLowerCase();
  if (lower.includes('tenant')) return 'tenantId';
  if (lower.includes('client id') || lower.includes('clientid')) return 'clientId';
  if (lower.includes('secret')) return 'clientSecret';
  return null;
}

export function mockSaveSso(
  status: SsoOidcStatus,
  form: SsoFormState,
  now: number,
): SsoOidcStatus {
  return {
    provider: status.provider,
    source: 'db',
    tenantId: form.tenantId.trim(),
    last4: form.clientSecret.trim().slice(-4) || 'abcd',
    rotatedAt: now,
    setByEmail: 'alex@example.com',
  };
}

export function mockClearSso(status: SsoOidcStatus): SsoOidcStatus {
  return {
    provider: status.provider,
    source: 'none',
    tenantId: null,
    last4: null,
    rotatedAt: null,
    setByEmail: null,
  };
}
